// src/pages/CRM/SFA/ProductList.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, IconButton, Chip,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Menu, Tooltip, CircularProgress, Alert, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Avatar, Card, CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as ProductIcon,
  Clear as ClearIcon,
  LocalOffer as PriceIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingIcon,
  Inventory2 as InventoryIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../utils/permissionUtils';
import { productService } from './sfaService';
import {
  ProductTypeLabels, ProductStatusLabels, ProductStatusColors,
  formatCurrency
} from './sfaEnums';
import ProductFormDialog from './ProductFormDialog';

const ProductList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState({ categories: [], subcategories: [], product_families: [] });
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: 20,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'ACTIVE',
    product_type: '',
    category: '',
    product_family: ''
  });

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Menu & Dialog state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
  const [formDialog, setFormDialog] = useState({ open: false, product: null });

  useEffect(() => {
    document.title = 'Product Catalog | Sales Force Automation';
    checkPermissions();
    loadCategories();
  }, []);

  useEffect(() => {
    if (canView) {
      loadProducts();
    }
  }, [canView, pagination.page, pagination.per_page, searchTerm, filters]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        per_page: pagination.per_page,
        search: searchTerm,
        ...filters
      };

      Object.keys(params).forEach(key => !params[key] && delete params[key]);

      const response = await productService.getAll(params);
      if (response.success) {
        setProducts(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      }
    } catch (err) {
      enqueueSnackbar('Failed to load products', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await productService.getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination(prev => ({
      ...prev,
      per_page: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'ACTIVE',
      product_type: '',
      category: '',
      product_family: ''
    });
    setSearchTerm('');
  };

  const handleMenuOpen = (event, product) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedProduct(null);
  };

  const handleDelete = async () => {
    if (!deleteDialog.product) return;

    try {
      await productService.delete(deleteDialog.product.id);
      enqueueSnackbar('Product deactivated', { variant: 'success' });
      setDeleteDialog({ open: false, product: null });
      loadProducts();
    } catch (err) {
      enqueueSnackbar('Failed to deactivate product', { variant: 'error' });
    }
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={ProductStatusLabels[status] || status}
        size="small"
        color={ProductStatusColors[status] || 'default'}
      />
    );
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        You don't have permission to view products.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <ProductIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Product Catalog
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage products and services for quoting
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Manage Categories">
            <Button
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/categories`)}
            >
              Categories
            </Button>
          </Tooltip>
          <Tooltip title="Price Books">
            <Button
              variant="outlined"
              startIcon={<PriceIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/price-books`)}
            >
              Price Books
            </Button>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadProducts}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setFormDialog({ open: true, product: null })}
            >
              New Product
            </Button>
          )}
        </Box>
      </Box>

      {/* Modern Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Products
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {pagination.total}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Across all categories
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <InventoryIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Categories
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {categories.categories?.length || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Main categories
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <CategoryIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Subcategories
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {categories.subcategories?.length || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Product subcategories
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <DashboardIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Product Families
                  </Typography>
                  <Typography variant="h3" fontWeight={700}>
                    {categories.product_families?.length || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Product lines
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 56,
                    height: 56
                  }}
                >
                  <TrendingIcon fontSize="large" />
                </Avatar>
              </Box>
            </CardContent>
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -20,
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)'
              }}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(ProductStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.product_type}
                label="Type"
                onChange={(e) => handleFilterChange('product_type', e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                {Object.entries(ProductTypeLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                label="Category"
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.categories?.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Family</InputLabel>
              <Select
                value={filters.product_family}
                label="Family"
                onChange={(e) => handleFilterChange('product_family', e.target.value)}
              >
                <MenuItem value="">All Families</MenuItem>
                {categories.product_families?.map((fam) => (
                  <MenuItem key={fam.name} value={fam.name}>{fam.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {(searchTerm || filters.product_type || filters.category || filters.product_family) && (
          <Box sx={{ mt: 2 }}>
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
              Clear Filters
            </Button>
          </Box>
        )}
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Standard Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">No products found</Typography>
                    {canModify && (
                      <Button
                        sx={{ mt: 2 }}
                        startIcon={<AddIcon />}
                        onClick={() => setFormDialog({ open: true, product: null })}
                      >
                        Create First Product
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow
                    key={product.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setFormDialog({ open: true, product })}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={product.image_url}
                          variant="rounded"
                          sx={{ bgcolor: 'primary.light' }}
                        >
                          <ProductIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {product.name}
                          </Typography>
                          {product.description && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                              {product.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {product.sku || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ProductTypeLabels[product.product_type] || product.product_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {product.category || '-'}
                      </Typography>
                      {product.product_family && (
                        <Typography variant="caption" color="text.secondary">
                          {product.product_family}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(product.standard_price, product.currency)}
                      </Typography>
                      {product.unit_cost && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Cost: {formatCurrency(product.unit_cost, product.currency)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(product.status)}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, product)}>
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.per_page}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setFormDialog({ open: true, product: selectedProduct });
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        {canModify && (
          <MenuItem
            onClick={() => {
              setDeleteDialog({ open: true, product: selectedProduct });
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Deactivate
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, product: null })}>
        <DialogTitle>Deactivate Product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate "{deleteDialog.product?.name}"?
            This will prevent it from being added to new quotes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, product: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Form Dialog */}
      <ProductFormDialog
        open={formDialog.open}
        product={formDialog.product}
        categories={categories}
        canModify={canModify}
        onClose={() => setFormDialog({ open: false, product: null })}
        onSave={() => {
          setFormDialog({ open: false, product: null });
          loadProducts();
          loadCategories();
        }}
      />
    </Box>
  );
};

export default ProductList;